import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';
import type { Row } from '@tanstack/react-table';

import { useEffect, useMemo } from 'react';

import { useFilesStorage } from '@/store/files-store';

import { useFilesTable } from './useFilesTable';

interface FsItems {
  dirs?: DirectoryPublic[];
  files?: FilePublicWithPermissions[];
}

type MixedItem = DirectoryPublic | FilePublicWithPermissions;

type ItemOrder = 'files-first' | 'directories-first';

interface UseFsDataOptions {
  /**
   * Controls how the table data set should be ordered before it reaches
   * react-table. Defaults to files-first to preserve previous behaviour.
   */
  tableOrder?: ItemOrder;
  /**
   * Controls how the derived navigation items should be ordered. Defaults
   * to directories-first for keyboard navigation focus.
   */
  navigationOrder?: ItemOrder;
  /**
   * When true, the hook syncs the computed navigation items with the
   * global files store so consumers no longer need bespoke effects.
   */
  syncStore?: boolean;
}

const DEFAULT_OPTIONS: Required<UseFsDataOptions> = {
  tableOrder: 'files-first',
  navigationOrder: 'directories-first',
  syncStore: false,
};

const orderItems = (directories: DirectoryPublic[], files: FilePublicWithPermissions[], order: ItemOrder): MixedItem[] => {
  if (order === 'directories-first') {
    return [...directories, ...files];
  }
  return [...files, ...directories];
};

export function useFsData(fsItems?: FsItems, options?: UseFsDataOptions) {
  const { dirs = [], files = [] } = fsItems ?? {};
  const { tableOrder, navigationOrder, syncStore } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  const tableData = useMemo<MixedItem[]>(() => orderItems(dirs, files, tableOrder), [dirs, files, tableOrder]);
  const dataLen = tableData.length;

  const { dataModel, sorting } = useFilesTable(tableData);
  const rows = dataModel.getRowModel().rows;

  const fileIds = useMemo(() => new Set(files.map((file) => file.id)), [files]);
  const directoryIds = useMemo(() => new Set(dirs.map((directory) => directory.id)), [dirs]);

  const filteredFileRows = useMemo(() => rows.filter((row): row is Row<FilePublicWithPermissions> => fileIds.has(row.original.id)), [rows, fileIds]);
  const filteredDirectoryRows = useMemo(() => rows.filter((row): row is Row<DirectoryPublic> => directoryIds.has(row.original.id)), [rows, directoryIds]);

  const navigationItems = useMemo<MixedItem[]>(
    () =>
      orderItems(
        filteredDirectoryRows.map((row) => row.original),
        filteredFileRows.map((row) => row.original),
        navigationOrder
      ),
    [filteredDirectoryRows, filteredFileRows, navigationOrder]
  );

  const totals = {
    files: filteredFileRows.length,
    directories: filteredDirectoryRows.length,
    all: dataLen,
  };

  const setAllItems = useFilesStorage((state) => state.setAllItems);
  useEffect(() => {
    if (!syncStore) return;
    setAllItems(navigationItems);
  }, [syncStore, navigationItems, setAllItems]);

  return {
    dataModel,
    sorting,
    files: filteredFileRows,
    directories: filteredDirectoryRows,
    totals,
    dataLen,
    allItems: navigationItems,
  };
}
